import { useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { useAction, useList } from '../api/queries'
import type { Role, User } from '../api/types'
import { DataTable, type Column } from '../components/DataTable'
import { Card, Field, Modal, PageHeader, StatusBadge } from '../components/ui'
import { formatDateTime, humanize } from '../lib/format'

const ROLES: Role[] = ['admin', 'engineer', 'inspector', 'viewer']

export function UsersPage() {
  const list = useList<User>('/api/users/', { page_size: 200 })
  const create = useAction<Record<string, unknown>>('post', '/api/users/', ['/api/users/'])
  const patch = useAction<{ id: number; body: Record<string, unknown> }>('patch', (b) => `/api/users/${b.id}/`, ['/api/users/'], (b) => b.body)
  const [open, setOpen] = useState(false)
  const [v, setV] = useState<Record<string, string>>({ role: 'inspector' })
  const [error, setError] = useState<ApiError | null>(null)
  const set = (k: string) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })
  async function submit(e: FormEvent) {
    e.preventDefault(); setError(null)
    try { await create.mutateAsync({ ...v, is_active: true }); setOpen(false); setV({ role: 'inspector' }) } catch (err) { setError(err as ApiError) }
  }
  const fe = error?.fieldErrors ?? {}
  const columns: Column<User>[] = [
    { key: 'u', header: 'Nombre de usuario', cell: (u) => <span className="mono">{u.username}</span> },
    { key: 'n', header: 'Nombre', cell: (u) => `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || '—' },
    { key: 'r', header: 'Rol', cell: (u) => <select aria-label={`Rol de ${u.username}`} value={u.role} onChange={(e) => patch.mutate({ id: u.id, body: { role: e.target.value } })}>{ROLES.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}</select> },
    { key: 'a', header: 'Activo', cell: (u) => <label className="check"><input type="checkbox" checked={u.is_active ?? true} onChange={(e) => patch.mutate({ id: u.id, body: { is_active: e.target.checked } })} /> {u.is_active ? <StatusBadge tone="ok" label="Activo" /> : <StatusBadge tone="neutral" label="Inactivo" />}</label> },
    { key: 'l', header: 'Último inicio de sesión', cell: (u) => formatDateTime(u.last_login), priority: 'low' },
  ]
  return (
    <div className="page">
      <PageHeader title="Usuarios" subtitle="Los usuarios se desactivan, nunca se eliminan" actions={<button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>Nuevo usuario</button>} />
      <Card><DataTable caption="Usuarios" columns={columns} rows={list.data?.results} loading={list.isPending} error={list.error} rowKey={(u) => u.id} /></Card>
      {open ? (
        <Modal title="Nuevo usuario" onClose={() => setOpen(false)}>
          <form className="form-stack" onSubmit={submit}>
            <Field label="Nombre de usuario" htmlFor="u-name" error={fe.username}><input id="u-name" value={v.username ?? ''} onChange={set('username')} autoComplete="off" /></Field>
            <Field label="Nombres" htmlFor="u-first"><input id="u-first" value={v.first_name ?? ''} onChange={set('first_name')} /></Field>
            <Field label="Apellidos" htmlFor="u-last"><input id="u-last" value={v.last_name ?? ''} onChange={set('last_name')} /></Field>
            <Field label="Correo electrónico" htmlFor="u-mail" error={fe.email}><input id="u-mail" type="email" value={v.email ?? ''} onChange={set('email')} /></Field>
            <Field label="Rol" htmlFor="u-role"><select id="u-role" value={v.role} onChange={set('role')}>{ROLES.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}</select></Field>
            <Field label="Contraseña" htmlFor="u-pw" error={fe.password} hint="Al menos 10 caracteres."><input id="u-pw" type="password" autoComplete="new-password" value={v.password ?? ''} onChange={set('password')} /></Field>
            {error && !Object.keys(fe).length ? <p role="alert" className="form-error">{error.message}</p> : null}
            <button className="btn btn--primary">Crear usuario</button>
          </form>
        </Modal>
      ) : null}
    </div>
  )
}
